// <!-- glow-menopause-style-enhancements -->
import React, { useState } from 'react';
import { ArrowRight, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import ConditionExitPopup from '../components/ConditionExitPopup';
import TrustStrip from '../components/TrustStrip';
import ClinicianCard from '../components/ClinicianCard';
import ServicesDropdown from '../components/ServicesDropdown';

const PostpartumLanding = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const scrollToBooking = () => {
    window.location.href = '/book';
  };

  const painPoints = [
    {
      title: 'A sadness that did not get the memo',
      pain: 'Are you smiling for everyone, holding the baby, posting the photos, and quietly wondering why you cannot feel any of it?',
      description: 'Postpartum depression is not a weakness or a "phase." It is a hormonal and neurochemical event, and it can be treated. You do not have to fake your way through this.'
    },
    {
      title: 'A rage that frightens you',
      pain: 'Are you snapping at your partner, your older kids, your own mother, then sobbing in the shower wondering when you became this person?',
      description: 'Postpartum rage is real and shockingly under-discussed. It is often hormonal, often thyroid-related, and almost always treatable. You are not a bad mom.'
    },
    {
      title: 'A body that feels like a stranger',
      pain: 'Are you staring in the mirror at hair falling out in clumps, weight that will not move, joints that hurt for no reason, and skin that is not yours?',
      description: 'Postpartum hormone crashes affect your thyroid, adrenals, sex hormones, and metabolism. Your body is not "just bouncing back slowly." It needs real support.'
    },
    {
      title: 'An exhaustion no nap can fix',
      pain: 'Have you slept and still felt empty? Do you drag yourself through every day pretending to function while inside you are running on fumes?',
      description: 'Postpartum fatigue that does not improve with rest is often a sign of thyroid dysfunction, anemia, adrenal depletion, or hormone imbalance, not "just being a mom."'
    },
    {
      title: 'A marriage you cannot find your way back to',
      pain: 'Has intimacy disappeared? Is your partner trying to help while you keep pulling away because you do not even feel like yourself anymore?',
      description: 'Plummeting estrogen and testosterone after birth tank libido, and breastfeeding compounds it. This is biology, not a verdict on your relationship, and there are real tools that help.'
    },
    {
      title: 'A version of yourself you cannot find',
      pain: 'Do you grieve the woman you were before the baby, the energy you used to have, the certainty that you were going to be okay?',
      description: 'Postpartum identity loss is one of the loneliest parts of motherhood. With the right care, the woman you are mourning is not gone, she is waiting for the support no one is giving you.'
    }
  ];

  const emotionalImpact = [
    {
      area: 'Your bond with baby',
      impact: 'Going through the motions of feeding, holding, soothing, while quietly waiting to feel the love everyone keeps describing'
    },
    {
      area: 'Your marriage',
      impact: 'A partner who is trying, intimacy that has disappeared, and resentment building underneath everyone\'s good intentions'
    },
    {
      area: 'Your career',
      impact: 'Returning to work in fog, doubting your competence, wondering if you can ever get back to the version of you that used to thrive'
    },
    {
      area: 'Your motherhood',
      impact: 'Snapping at your kids, missing presence with them, then crying yourself to sleep with guilt for being the mother you swore you would never be'
    }
  ];

  const steps = [
    {
      number: '1',
      title: 'Tell us how you are really doing',
      description: 'Not the version you tell your in-laws. The real version. Share what you have not told anyone, we listen without judgment.'
    },
    {
      number: '2',
      title: 'Get a real postpartum hormone evaluation',
      description: 'Comprehensive testing of thyroid, sex hormones, adrenals, iron, and key nutrients to find what is actually driving how you feel.'
    },
    {
      number: '3',
      title: 'Receive a personalized plan',
      description: 'Targeted support, bioidentical hormones when appropriate, thyroid optimization, nutrient repletion, and breastfeeding-safe protocols when needed.'
    },
    {
      number: '4',
      title: 'Get yourself back, one week at a time',
      description: 'Ongoing care, frequent check-ins, and a clinician who knows that postpartum recovery is not 6 weeks. It is a season, and you deserve support through all of it.'
    }
  ];

  const proofPoints = [
    {
      stat: '1 in 5',
      title: 'Women experience postpartum mood disorders',
      description: 'You are not rare. Postpartum depression, anxiety, and rage are extraordinarily common, they are just rarely talked about honestly.'
    },
    {
      stat: '12-18 mo',
      title: 'For hormones to rebalance',
      description: 'The "6-week postpartum check" is not enough. Hormonal recovery from pregnancy and birth often takes more than a year, and longer if you are breastfeeding.'
    },
    {
      stat: '50%+',
      title: 'Of postpartum thyroid issues missed',
      description: 'Postpartum thyroiditis affects up to 1 in 12 women and is routinely missed. Most postpartum fatigue and mood symptoms have a thyroid component no one tested for.'
    }
  ];

  const faqs = [
    {
      question: 'How do I know if this is postpartum depression vs. just being tired?',
      answer: 'Normal postpartum exhaustion improves over time and lifts when you sleep. Postpartum depression, anxiety, and rage do not. If you feel persistently sad, numb, anxious, irritable, hopeless, or detached from your baby, for more than two weeks, regardless of sleep, it is time to be evaluated. You do not need to be in crisis to deserve help.'
    },
    {
      question: 'I am breastfeeding. Can you still help me?',
      answer: 'Absolutely. We design every plan with breastfeeding compatibility in mind. There are safe, evidence-based options for thyroid optimization, hormone support, mood management, and nutrient repletion that work alongside breastfeeding rather than against it.'
    },
    {
      question: 'My doctor said my labs are "normal." Why do I still feel terrible?',
      answer: 'Standard postpartum lab ranges are population averages, not personal optimums. Many women have "normal" thyroid, "normal" iron, and "normal" hormones on paper while feeling completely unwell. We look at the full picture, labs, symptoms, history, and context, to find what is actually going on.'
    },
    {
      question: 'How is this different from talking to my OB or PCP?',
      answer: 'Most OBs and PCPs are wonderful at acute care, but they were not trained in nuanced postpartum hormone medicine. We specialize in this, thyroid, sex hormones, adrenals, metabolic health, and the way they interact uniquely after pregnancy. You get more time, more testing, and a treatment plan built around your full picture.'
    },
    {
      question: 'I have had thoughts about hurting myself or not being here. Is that postpartum?',
      answer: 'Severe intrusive thoughts, thoughts of self-harm, or thoughts that your family would be better off without you can occur in postpartum depression and postpartum psychosis, and they are medically urgent. If you are in crisis right now, please call or text 988. Then book a consultation so we can build a plan to keep you safe and well.'
    }
  ];

  return (
    <div className="pcos-page">
      {/* Header */}
      <header className="pcos-header">
        <div className="pcos-container pcos-header-inner">
          <a href="/" className="pcos-brand" data-testid="postpartum-brand-link">
            <img
              src="https://customer-assets.emergentagent.com/job_hormone-wellness-1/artifacts/emqp5y2s_symbol%20%281%29.svg"
              alt="Glow Health Logo"
              className="brand-symbol"
            />
            <span>Glow Health</span>
          </a>
          <div className="pcos-header-actions">
            <ServicesDropdown testIdPrefix="postpartum-nav-services" />
            <button className="pcos-btn pcos-btn-primary" onClick={scrollToBooking} data-testid="postpartum-header-cta">
              Book Your Free Consultation
            </button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="pcos-hero">
          <div className="pcos-container pcos-hero-split">
            <div className="pcos-hero-content">
              <span className="pcos-eyebrow">Postpartum Care</span>
              <h1 className="pcos-hero-headline">
                You are not "just hormonal."<br />
                <span className="pcos-highlight">You are postpartum, and you deserve real care.</span>
              </h1>
              <p className="pcos-hero-sub">
                If everyone keeps telling you it gets better, if your six-week check-up said you were "fine" while you knew you were not, if you are tired of pretending, you are not failing at motherhood. Your hormones, your thyroid, your body, and your nervous system have been through something enormous. They need support. So do you.
              </p>
              <div className="pcos-hero-actions">
                <button className="pcos-btn pcos-btn-primary pcos-btn-large" onClick={scrollToBooking} data-testid="postpartum-hero-cta">
                  Get Real Support
                  <ArrowRight size={18} />
                </button>
                <a href="#pain" className="pcos-btn pcos-btn-secondary" data-testid="postpartum-hero-secondary-cta">See If This Sounds Like You</a>
              </div>
              <p className="pcos-hero-note">Free consultation. No judgment. Finally, someone who actually listens.</p>
              <div className="meno-hero-credentials" data-testid="postpartum-hero-credentials">
                <p><strong>Utah-licensed clinician</strong> experienced in postpartum hormone care.</p>
                <p>Serving women in Utah via secure HIPAA video visits.</p>
              </div>
            </div>
            <div className="pcos-hero-image">
              <img
                src="https://images.unsplash.com/photo-1502323777036-f29e3972d82f?w=640&h=760&fit=crop"
                alt="A woman who finally got the care she needed"
                loading="lazy"
              />
              <div className="pcos-hero-image-badge">
                "I got to be me again."
              </div>
            </div>
          </div>
        </section>

        <TrustStrip />

        {/* Three-panel journey strip */}
        <section className="meno-journey" data-testid="postpartum-journey">
          <div className="pcos-container">
            <div className="meno-journey-grid">
              <figure className="meno-journey-panel" data-testid="postpartum-journey-panel-1">
                <div className="meno-journey-image">
                  <img src="/postpartum-panel-1.png" alt="A tired new mother on a nursery glider holding her baby in the morning light" loading="lazy" />
                </div>
                <figcaption>You are not failing. Your hormones are still recalibrating.</figcaption>
              </figure>
              <figure className="meno-journey-panel" data-testid="postpartum-journey-panel-2">
                <div className="meno-journey-image">
                  <img src="/postpartum-panel-2.png" alt="A new mother on a calm telehealth video visit while baby naps nearby" loading="lazy" />
                </div>
                <figcaption>Talk with a clinician who understands postpartum hormones.</figcaption>
              </figure>
              <figure className="meno-journey-panel" data-testid="postpartum-journey-panel-3">
                <div className="meno-journey-image">
                  <img src="/postpartum-panel-3.png" alt="A mother walking outdoors in the sun holding her baby, feeling like herself again" loading="lazy" />
                </div>
                <figcaption>A plan that respects your body, your baby, and your time.</figcaption>
              </figure>
            </div>
          </div>
        </section>

        {/* Specialist value prop */}
        <section className="meno-specialist" data-testid="postpartum-specialist">
          <div className="pcos-container">
            <p><strong>Not a general telehealth clinic.</strong> We specialize in postpartum hormones, so your symptoms are not minimized as &quot;just baby blues&quot; or &quot;just sleep deprivation.&quot;</p>
          </div>
        </section>


        {/* Pain Points */}
        <section className="pcos-section pcos-pain-section" id="pain">
          <div className="pcos-container">
            <div className="pcos-section-head">
              <span className="pcos-eyebrow">Does This Sound Like You?</span>
              <h2>The postpartum nobody warned you about</h2>
              <p>It is not "the baby blues." It is not "just tired." Real postpartum recovery takes more than 6 weeks, and most women never get the care they actually needed.</p>
            </div>
            <div className="pcos-pain-grid">
              {painPoints.map((point, index) => (
                <div key={index} className="pcos-pain-card" data-testid={`postpartum-pain-card-${index}`}>
                  <h3>{point.title}</h3>
                  <p className="pcos-pain-question">{point.pain}</p>
                  <p className="pcos-pain-explain">{point.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Emotional Impact */}
        <section className="pcos-section pcos-impact-section">
          <div className="pcos-container">
            <div className="pcos-impact-wrap">
              <div className="pcos-impact-content">
                <span className="pcos-eyebrow">The Hidden Cost</span>
                <h2>Postpartum is not just about the baby. It is about the mother no one is checking on.</h2>
                <p className="pcos-impact-intro">
                  The world keeps asking how the baby is sleeping. Almost no one is asking how YOU are.
                </p>
              </div>
              <div className="pcos-impact-grid">
                {emotionalImpact.map((item, index) => (
                  <div key={index} className="pcos-impact-card" data-testid={`postpartum-impact-card-${index}`}>
                    <h4>{item.area}</h4>
                    <p>{item.impact}</p>
                  </div>
                ))}
              </div>
              <p className="pcos-impact-cta">
                <strong>You are not "just adjusting." You are recovering. And you deserve real medical support, not platitudes.</strong>
              </p>
            </div>
          </div>
        </section>

        {/* Quote */}
        <section className="pcos-section pcos-quote-section" style={{backgroundImage: "url('/quote-bg-postpartum.png')"}}>
          <div className="pcos-container">
            <div className="pcos-quote-wrap">
              <blockquote>
                <p>"Everyone kept telling me to enjoy this season. I was hiding in my bathroom googling whether I was losing my mind. Turns out it was my thyroid. I just needed someone to actually look."</p>
                <cite>Real patient story</cite>
              </blockquote>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="pcos-section" id="plan">
          <div className="pcos-container">
            <div className="pcos-section-head">
              <span className="pcos-eyebrow">How We Help</span>
              <h2>A real approach to postpartum recovery</h2>
              <p>No more "give it time." No more 6-week checkups that send you home unwell. No more being treated like the baby is the only patient.</p>
            </div>
            <div className="pcos-steps-grid">
              {steps.map((step, index) => (
                <div key={index} className="pcos-step-card" data-testid={`postpartum-step-card-${index}`}>
                  <div className="pcos-step-number">{step.number}</div>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
              ))}
            </div>
            <div className="pcos-steps-cta">
              <button className="pcos-btn pcos-btn-primary pcos-btn-large" onClick={scrollToBooking} data-testid="postpartum-steps-cta">
                Book Your Free Consultation
                <ArrowRight size={18} />
              </button>
              <p className="pcos-cta-note">No commitment. No pressure. Just real care.</p>
            </div>
          </div>
        </section>

        {/* Proof Points */}
        <section className="pcos-section pcos-proof-section">
          <div className="pcos-container">
            <div className="pcos-section-head">
              <span className="pcos-eyebrow">The Reality</span>
              <h2>You are not alone, even though it feels that way</h2>
            </div>
            <div className="pcos-proof-grid">
              {proofPoints.map((point, index) => (
                <div key={index} className="pcos-proof-card" data-testid={`postpartum-proof-card-${index}`}>
                  <strong>{point.stat}</strong>
                  <h3>{point.title}</h3>
                  <p>{point.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <ClinicianCard />

        {/* Who this is right for */}
        <section className="pcos-section meno-fit-section" data-testid="postpartum-fit-section">
          <div className="pcos-container">
            <div className="pcos-section-head">
              <span className="pcos-eyebrow">A Quick Honest Check</span>
              <h2>Who My Glow Health is right for</h2>
              <p>We are not for everyone, and that is the point. Here is who tends to feel the biggest shift working with us.</p>
            </div>
            <div className="meno-fit-grid">
              <div className="meno-fit-card meno-fit-yes" data-testid="postpartum-fit-yes">
                <h3>This is right for you if</h3>
                <ul>
                  <li>You live in Utah and want secure telehealth visits.</li>
                  <li>You are within roughly 3 years postpartum and not feeling like yourself.</li>
                  <li>You want a clinician who specializes in postpartum hormones, not a generalist.</li>
                  <li>You are open to evidence-based hormone, thyroid, and mood support.</li>
                  <li>You want a plan that fits real new-mom life, including breastfeeding if it applies.</li>
                </ul>
              </div>
              <div className="meno-fit-card meno-fit-no" data-testid="postpartum-fit-no">
                <h3>This is probably not for you if</h3>
                <ul>
                  <li>You are looking for routine well-baby care for your child.</li>
                  <li>You want a quick prescription with no lab workup or follow-up.</li>
                  <li>You are not interested in addressing root-cause hormone changes.</li>
                  <li>You prefer a one-size-fits-all mood or wellness program.</li>
                </ul>
              </div>
            </div>
          </div>
        </section>


        {/* CTA Section */}
        <section className="pcos-section pcos-cta-section" id="book">
          <div className="pcos-container">
            <div className="pcos-cta-wrap">
              <div className="pcos-cta-content">
                <span className="pcos-eyebrow">Take The First Step</span>
                <h2>Stop disappearing into motherhood</h2>
                <ul className="pcos-check-list">
                  <li>
                    <CheckCircle size={18} />
                    <span>Finally get a clinician who treats the mother, not just the baby's mom</span>
                  </li>
                  <li>
                    <CheckCircle size={18} />
                    <span>Get a postpartum plan that actually addresses your hormones, thyroid, and energy</span>
                  </li>
                  <li>
                    <CheckCircle size={18} />
                    <span>Stop being told "this is normal" when nothing about how you feel is okay</span>
                  </li>
                  <li>
                    <CheckCircle size={18} />
                    <span>Find your way back to yourself, without giving up on motherhood to do it</span>
                  </li>
                </ul>
                <button className="pcos-btn pcos-btn-primary pcos-btn-large" onClick={scrollToBooking} data-testid="postpartum-mid-cta">
                  Book Your Free Consultation
                  <ArrowRight size={18} />
                </button>
                <p className="pcos-cta-note">Free & confidential. Limited spots available each week.</p>
              </div>
              <div className="pcos-cta-visual">
                <div className="pcos-guarantee-card">
                  <h3>Our Promise</h3>
                  <p>If you do not feel meaningfully better within 60 days of starting your plan, we will keep working with you for free until you do. No questions asked.</p>
                </div>
                <div className="pcos-testimonial-card">
                  <p>"Eight months after my second baby, someone finally tested everything. Two months later, I felt like a person again. I should not have had to wait this long."</p>
                  <span>Postpartum patient, age 34</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="pcos-section">
          <div className="pcos-container">
            <div className="pcos-section-head">
              <span className="pcos-eyebrow">Questions</span>
              <h2>What you are probably wondering</h2>
            </div>
            <div className="pcos-faq-list">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className={`pcos-faq-item ${openFaq === index ? 'open' : ''}`}
                  data-testid={`postpartum-faq-item-${index}`}
                >
                  <button
                    className="pcos-faq-question"
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    data-testid={`postpartum-faq-toggle-${index}`}
                  >
                    <span>{faq.question}</span>
                    {openFaq === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                  {openFaq === index && (
                    <div className="pcos-faq-answer">
                      <p>{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="pcos-section pcos-final-cta">
          <div className="pcos-container">
            <div className="pcos-final-cta-content">
              <h2>You have given enough of yourself away</h2>
              <p>
                Every day you wait is another day of running on empty for everyone but you. You can love your baby AND want yourself back. You can be grateful AND need real help. Both can be true. Both deserve to be true.
              </p>
              <button className="pcos-btn pcos-btn-primary pcos-btn-large" onClick={scrollToBooking} data-testid="postpartum-final-cta">
                Book Your Free Consultation Now
                <ArrowRight size={18} />
              </button>
              <p className="pcos-final-note">15 minutes could change how you experience this whole season.</p>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="pcos-footer">
        <div className="pcos-container pcos-footer-inner">
          <span>© {new Date().getFullYear()} Glow Health LLC</span>
          <div className="pcos-footer-links">
            <a href="/privacy-policy">Privacy Policy</a>
            <span>·</span>
            <a href="/terms-of-service">Terms of Service</a>
          </div>
        </div>
      </footer>
      <ConditionExitPopup condition="postpartum" />
    </div>
  );
};

export default PostpartumLanding;
